import { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowUpRight } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "../ui/dialog";
import { Reveal, SectionTag, BigHead } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FILTERS = ["All", "Branding", "Web", "Print", "Marketing"];

const PROJECTS = [
  {
    id: "lumen", name: "Lumen & Co. Coffee", cat: "Branding", span: "md:col-span-7",
    img: "https://picsum.photos/seed/stax-lumen/1200/800",
    art: { bg: "#FFE600" },
    challenge: "A neighborhood roaster with great beans and zero shelf presence.",
    strategy: "Built a bold identity around warmth and ritual — oversized type, sunny yellow, and packaging that waves at you from the shelf.",
    outcome: "Full rebrand: logo system, packaging, signage, and social templates. Retail placements up 3x in six months.",
  },
  {
    id: "pulse", name: "Pulse Athletics", cat: "Web", span: "md:col-span-5",
    img: "https://picsum.photos/seed/stax-pulse/1000/900",
    art: { bg: "#00AEEF" },
    challenge: "A gym whose website felt like a spreadsheet — signups were stalling.",
    strategy: "High-energy editorial layout, class-first navigation, and a booking flow with zero dead ends.",
    outcome: "New site + brand refresh. Online trial signups up 140% in the first quarter.",
  },
  {
    id: "field", name: "Field Notes Market", cat: "Print", span: "md:col-span-5",
    img: "https://picsum.photos/seed/stax-field/1000/900",
    art: { bg: "#EC008C" },
    challenge: "A farmers market collective that needed printed materials people actually keep.",
    strategy: "A print-first system: posters, vendor cards, and a seasonal zine with a collectible feel.",
    outcome: "12-piece print suite. Foot traffic up, and the zine now has a waitlist.",
  },
  {
    id: "rocket", name: "Rocket Taco", cat: "Branding", span: "md:col-span-7",
    img: "https://picsum.photos/seed/stax-rocket/1200/800",
    art: { bg: "#0A0A0A" },
    challenge: "A food truck with a cult following but an identity held together by clip art.",
    strategy: "Loud, playful branding built for wraps, merch, and Instagram — CMYK color and a mascot with attitude.",
    outcome: "Truck wrap, menu boards, merch line. Second truck launched within a year.",
  },
  {
    id: "nova", name: "Nova Skin Studio", cat: "Marketing", span: "md:col-span-6",
    img: "https://picsum.photos/seed/stax-nova/1100/850",
    art: { bg: "#FFE600" },
    challenge: "A skincare studio invisible in a crowded local market.",
    strategy: "Campaign system mixing print mailers, social ads, and in-studio signage — one look, every touchpoint.",
    outcome: "Booked out 6 weeks in advance after the launch campaign.",
  },
  {
    id: "atlas", name: "Atlas Realty Group", cat: "Web", span: "md:col-span-6",
    img: "https://picsum.photos/seed/stax-atlas/1100/850",
    art: { bg: "#00AEEF" },
    challenge: "A boutique realty team whose site looked like every competitor's template.",
    strategy: "Editorial, magazine-style listings with bold type and photography-forward layouts.",
    outcome: "Time on site doubled; listing inquiries up 85%.",
  },
  {
    id: "nanas-obituary", name: "Nana's Obituary", cat: "Print", span: "md:col-span-6",
    img: "https://picsum.photos/seed/stax-nana/1100/850",
    art: { bg: "#EC008C" },
    pdfRef: "nanas-obituary",
    challenge: "Honor a loved one's life with a printed piece the family would keep forever.",
    strategy: "An 11×17 memorial program designed to fold to 8.5×11 — a photo-forward tribute layout with service details, order of service, and room for memories.",
    outcome: "A keepsake program the family treasures — designed press-ready and folded with care.",
  },
];

const ProjectVisual = ({ p, src, big = false }) => {
  const real = src.startsWith(API);
  return (
  <div
    className={`group/img relative overflow-hidden ${big ? "min-h-[300px] sm:min-h-[360px]" : "min-h-[220px] md:min-h-[260px]"}`}
    style={{ clipPath: "polygon(0 0, 100% 0, 100% calc(100% - 26px), calc(100% - 26px) 100%, 0 100%)" }}
  >
    <img
      src={src}
      alt={p.name}
      loading="lazy"
      className={real
        ? "absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover/img:scale-[1.04]"
        : "absolute inset-0 h-full w-full object-cover grayscale contrast-125 transition-all duration-700 group-hover/img:grayscale-0 group-hover/img:scale-[1.06]"}
    />
    {!real && (
      <div
        className="absolute inset-0 mix-blend-multiply transition-opacity duration-500 group-hover/img:opacity-10"
        style={{ background: p.art.bg, opacity: 0.4 }}
      />
    )}
    <div
      aria-hidden="true"
      className="absolute inset-0 opacity-0 transition-opacity duration-300 group-hover/img:opacity-100"
      style={{ background: "radial-gradient(300px circle at var(--mx, 50%) var(--my, 50%), rgba(255,255,255,0.4), transparent 70%)" }}
    />
    {!real && (
      <span
        className="absolute left-5 bottom-4 font-display uppercase leading-[0.85] text-white drop-shadow-[3px_3px_0_rgba(10,10,10,0.9)]"
        style={{ fontSize: big ? "4.5rem" : "3rem" }}
      >
        {p.name.split(" ")[0]}
      </span>
    )}
    <span className="absolute right-4 top-4 border-2 border-ink bg-white px-2.5 py-1 font-mono2 text-[10px] font-bold uppercase tracking-[0.25em] text-ink">
      {p.cat}
    </span>
  </div>
  );
};

export const Portfolio = ({ ch = "03" }) => {
  const [filter, setFilter] = useState("All");
  const [active, setActive] = useState(null);
  const [overrides, setOverrides] = useState({});
  const shown = PROJECTS.filter((p) => filter === "All" || p.cat === filter);

  useEffect(() => {
    axios.get(`${API}/portfolio/images`).then((r) => setOverrides(r.data)).catch(() => {});
  }, []);

  const srcFor = (p) => (overrides[p.id] ? `${API}/files/${overrides[p.id].file_id}` : p.img);

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.setProperty("--mx", `${e.clientX - r.left}px`);
    e.currentTarget.style.setProperty("--my", `${e.clientY - r.top}px`);
  };

  return (
    <section id="work" data-testid="portfolio-section" className="relative bg-paper py-24 sm:py-32 overflow-hidden">
      <div className="relative mx-auto max-w-7xl px-4 sm:px-8">
        <Reveal><SectionTag ch={ch}>Portfolio</SectionTag></Reveal>
        <div className="mt-6 flex flex-wrap items-end justify-between gap-8">
          <Reveal delay={0.08}>
            <BigHead>The Work<span className="text-staxcyan">.</span></BigHead>
          </Reveal>
          <Reveal delay={0.14}>
            <div data-testid="portfolio-filters" className="flex flex-wrap gap-2">
              {FILTERS.map((f) => (
                <button
                  key={f}
                  data-testid={`filter-${f.toLowerCase()}`}
                  onClick={() => setFilter(f)}
                  className={`border-2 border-ink px-4 py-2 font-mono2 text-xs font-bold uppercase tracking-[0.2em] transition-colors ${
                    filter === f ? "bg-ink text-white" : "bg-white text-ink hover:bg-staxyellow"
                  }`}
                >
                  {f}
                </button>
              ))}
            </div>
          </Reveal>
        </div>

        <motion.div layout className="mt-14 grid grid-cols-1 md:grid-cols-12 gap-6">
          <AnimatePresence mode="popLayout">
            {shown.map((p) => (
              <motion.button
                layout
                key={p.id}
                data-testid={`project-card-${p.id}`}
                onClick={() => setActive(p)}
                onMouseMove={onMove}
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.35 }}
                className={`group relative border-4 border-ink bg-white text-left shadow-hard-sm transition-transform hover:-translate-y-1.5 hover:shadow-hard ${p.span}`}
              >
                <div className="relative overflow-hidden">
                  <ProjectVisual p={p} src={srcFor(p)} />
                  <div className="absolute inset-0 flex items-center justify-center bg-ink/0 transition-colors duration-300 group-hover:bg-ink/60 pointer-events-none">
                    <span className="flex items-center gap-2 border-2 border-white bg-white px-5 py-3 font-display uppercase text-lg text-ink opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                      {p.name} <ArrowUpRight size={18} />
                    </span>
                  </div>
                </div>
                <div className="flex items-center justify-between border-t-4 border-ink px-5 py-3">
                  <span className="font-display uppercase text-lg">{p.name}</span>
                  <span className="font-mono2 text-[10px] font-bold uppercase tracking-[0.25em] text-ink/50">
                    {p.cat}
                  </span>
                </div>
              </motion.button>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>

      <Dialog open={!!active} onOpenChange={() => setActive(null)}>
        <DialogContent
          data-testid="case-study-modal"
          data-lenis-prevent
          className="border-4 border-ink bg-paper p-0 sm:max-w-3xl rounded-none shadow-hard gap-0 max-h-[90vh] overflow-y-auto [&>button]:hidden"
        >
          {active && (
            <>
              <div className="flex items-center justify-between border-b-4 border-ink bg-white px-6 py-4 sticky top-0 z-10">
                <span className="font-mono2 text-xs font-bold uppercase tracking-[0.3em] text-ink/60">
                  Case Study — {active.cat}
                </span>
                <button data-testid="case-study-close" onClick={() => setActive(null)} aria-label="Close"
                  className="border-2 border-ink bg-staxyellow p-1 hover:bg-staxmagenta hover:text-white transition-colors">
                  <X size={18} />
                </button>
              </div>
              <ProjectVisual p={active} src={srcFor(active)} big />
              <div className="p-6 sm:p-10">
                <DialogTitle className="font-display uppercase text-4xl sm:text-5xl text-ink">{active.name}</DialogTitle>
                <div className="mt-8 space-y-8">
                  {[
                    ["The Challenge", active.challenge, "bg-staxyellow"],
                    ["The Strategy", active.strategy, "bg-staxcyan"],
                    ["The Outcome", active.outcome, "bg-staxmagenta text-white"],
                  ].map(([t, body, cls]) => (
                    <div key={t} className="grid grid-cols-1 sm:grid-cols-[180px_1fr] gap-3 items-start">
                      <span className={`inline-block w-fit border-2 border-ink px-3 py-1.5 font-display uppercase text-sm ${cls}`}>
                        {t}
                      </span>
                      <p className="text-base font-medium text-ink/75 leading-relaxed">{body}</p>
                    </div>
                  ))}
                </div>
                {active.pdfRef && (
                  <a
                    data-testid="case-study-pdf-link"
                    href={`${API}/files/by-ref/misc/${active.pdfRef}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group mt-8 inline-flex items-center gap-2 border-2 border-ink bg-staxmagenta px-6 py-3.5 font-display uppercase text-sm text-white shadow-hard-sm transition-transform hover:-translate-y-0.5"
                  >
                    View the full printed piece (PDF) <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </a>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};
